import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, MoreHorizontal, Search, Download, Music, Video, Eye, Disc3, XCircle, RotateCcw } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { StatusPill, EmptyState, TableWrap, th, td, fmtDate } from "@/components/dashboard/ui";
import ReleaseDetailDialog from "@/components/releases/ReleaseDetailDialog";

const STATUSES = ["Draft", "In Review", "Live", "Rejected"];

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function AdminReleases({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [detailId, setDetailId] = useState(null);
  // Reason dialog state
  const [reasonFor, setReasonFor] = useState(null); // { release, action: 'reject'|'send-back' }
  const [reasonText, setReasonText] = useState("");
  const [savingReason, setSavingReason] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/releases");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load releases");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setReleaseStatus = async (id, s) => {
    try {
      await api.patch(`/admin/releases/${id}`, { status: s });
      toast.success(`Release marked ${s}`);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    }
  };

  const openReason = (release, action) => { setReasonFor({ release, action }); setReasonText(""); };

  const submitReason = async () => {
    if (!reasonFor) return;
    const text = reasonText.trim();
    if (!text) {
      toast.error(reasonFor.action === "reject" ? "Please add a rejection reason." : "Please add a note explaining what needs to be fixed.");
      return;
    }
    setSavingReason(true);
    const status = reasonFor.action === "reject" ? "Rejected" : "Draft";
    try {
      await api.patch(`/admin/releases/${reasonFor.release.release_id}`, { status, reason: text });
      toast.success(reasonFor.action === "reject" ? "Release rejected — artist notified." : "Sent back to draft — artist notified.");
      setReasonFor(null); setReasonText("");
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't save your decision"));
    } finally {
      setSavingReason(false);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (type !== "all" && r.type !== type) return false;
      if (!q) return true;
      return [r.title, r.artist_name, r.display_artist, r.label, r.upc, r.catalog_number, r.created_by_name, r.created_by_email]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(q));
    });
  }, [rows, query, status, type]);

  const statusCounts = useMemo(() => {
    const counts = { all: rows.length };
    for (const s of STATUSES) counts[s] = 0;
    for (const r of rows) counts[r.status] = (counts[r.status] || 0) + 1;
    return counts;
  }, [rows]);

  const exportCsv = () => {
    const headers = [
      "Type","Title","Version","Display Artist","Label","Created By","Created By Email",
      "UPC","Catalog #","Tracks","Status","Submitted","Release Date","Created",
    ];
    const lines = [headers.join(",")];
    for (const r of filtered) {
      lines.push([
        r.type, r.title, r.version || "", r.display_artist || r.artist_name || "",
        r.label || "", r.created_by_name || "", r.created_by_email || "",
        r.upc || "", r.catalog_number || "", r.tracks_count ?? 0,
        r.status, r.submitted_at || "", r.release_date || "", r.created_at || "",
      ].map(csvEscape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `noizbloc-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-releases">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">Submissions</h2>
          <p className="mt-1.5 text-slate-400">Review every draft, in-review and live product across the roster.</p>
        </div>
        <button
          data-testid="export-submissions-csv"
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-5 py-2.5 hover:bg-slate-200 disabled:opacity-40 transition-all shrink-0"
        >
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="submissions-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, artist, label, UPC, catalog…"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
          />
        </div>
        <div className="flex items-center gap-1 border border-white/10 rounded-xl bg-white/[0.02] p-1">
          {[
            { key: "all", label: "All types" },
            { key: "Single", label: "Single" },
            { key: "EP", label: "EP" },
            { key: "Album", label: "Album" },
            { key: "Music Video", label: "Video", icon: Video },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`submissions-type-${t.key.replace(/ /g, "-").toLowerCase()}`}
              onClick={() => setType(t.key)}
              className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${type === t.key ? "bg-white text-black" : "text-slate-300 hover:text-white"}`}
            >
              {t.icon && <t.icon size={12} />} {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-white/10 overflow-x-auto">
        {[
          { key: "all", label: `All (${statusCounts.all || 0})` },
          ...STATUSES.map((s) => ({ key: s, label: `${s} (${statusCounts[s] || 0})` })),
        ].map((tab) => (
          <button
            key={tab.key}
            data-testid={`submissions-status-${tab.key.replace(/ /g, "-").toLowerCase()}`}
            onClick={() => setStatus(tab.key)}
            className={`px-4 py-2.5 text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ${
              status === tab.key ? "border-white text-white" : "border-transparent text-slate-400 hover:text-white"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No submissions in this view" desc={rows.length ? "Try clearing filters or search." : "Releases created by artists will show up here."} />
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <th className={th}>Type</th>
              <th className={th}>Title (Version)</th>
              <th className={th}>Display Artist</th>
              <th className={th}>Label</th>
              <th className={th}>Created By</th>
              <th className={th}>Submitted</th>
              <th className={th}>Release Date</th>
              <th className={th}>Tracks</th>
              <th className={th}>Status</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const isVideo = r.type === "Music Video";
              return (
                <tr key={r.release_id} data-testid={`release-row-${r.release_id}`} className="hover:bg-white/[0.02]">
                  <td className={td}>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
                      {isVideo ? <Video size={12} className="text-fuchsia-400" /> : <Music size={12} className="text-blue-400" />}
                      {r.type}
                    </span>
                  </td>
                  <td className={td + " text-white"}>
                    <button
                      type="button"
                      data-testid={`release-open-${r.release_id}`}
                      onClick={() => setDetailId(r.release_id)}
                      className="text-left flex items-center gap-3 group"
                    >
                      {r.cover_url ? (
                        <img
                          data-testid={`release-thumb-${r.release_id}`}
                          src={mediaUrl(r.cover_url)}
                          alt=""
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling?.classList.remove("hidden"); }}
                          className="h-11 w-11 rounded-md object-cover border border-white/10 shrink-0 group-hover:border-white/25 transition-colors"
                        />
                      ) : null}
                      <div className={`h-11 w-11 rounded-md border border-white/10 bg-white/[0.03] flex items-center justify-center shrink-0 ${r.cover_url ? "hidden" : ""}`}>
                        <Disc3 size={18} className="text-slate-500" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate group-hover:underline">{r.title}{r.version ? <span className="text-slate-500 font-normal"> ({r.version})</span> : null}</div>
                        <div className="text-xs text-slate-500 truncate">{r.upc ? `UPC ${r.upc}` : "UPC pending"}{r.catalog_number ? ` · ${r.catalog_number}` : ""}</div>
                      </div>
                    </button>
                  </td>
                  <td className={td}>{r.display_artist || r.artist_name}</td>
                  <td className={td}>{r.label || "—"}</td>
                  <td className={td}>
                    <div className="text-white text-sm">{r.created_by_name || "—"}</div>
                    <div className="text-xs text-slate-500">{r.created_by_email || ""}</div>
                  </td>
                  <td className={td}>{r.submitted_at ? fmtDate(r.submitted_at) : "—"}</td>
                  <td className={td}>{r.release_date || "—"}</td>
                  <td className={td}>{r.tracks_count ?? 0}</td>
                  <td className={td}><StatusPill status={r.status} /></td>
                  <td className={td + " text-right"}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`release-actions-${r.release_id}`} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
                          <MoreHorizontal size={18} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#0b1220] border-white/10 text-white">
                        <DropdownMenuItem onClick={() => setDetailId(r.release_id)} className="focus:bg-white/10 cursor-pointer" data-testid={`release-view-${r.release_id}`}>
                          <Eye size={14} className="mr-2" /> View & download
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setReleaseStatus(r.release_id, "Live")} className="text-emerald-400 focus:bg-white/10 focus:text-emerald-400 cursor-pointer" data-testid={`release-approve-${r.release_id}`}>
                          Approve &amp; go live
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openReason(r, "send-back")} className="text-amber-300 focus:bg-white/10 focus:text-amber-300 cursor-pointer" data-testid={`release-sendback-${r.release_id}`}>
                          <RotateCcw size={14} className="mr-2" /> Send back to draft…
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openReason(r, "reject")} className="text-red-400 focus:bg-white/10 focus:text-red-400 cursor-pointer" data-testid={`release-reject-${r.release_id}`}>
                          <XCircle size={14} className="mr-2" /> Reject with reason…
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
      )}

      <ReleaseDetailDialog
        open={!!detailId}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        releaseId={detailId}
      />

      {/* Reject / Send-back reason dialog */}
      <Dialog open={!!reasonFor} onOpenChange={(o) => { if (!o) { setReasonFor(null); setReasonText(""); } }}>
        <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-lg" data-testid="reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              {reasonFor?.action === "reject" ? (
                <><XCircle size={20} className="text-red-400" /> Reject release</>
              ) : (
                <><RotateCcw size={20} className="text-amber-300" /> Send back to draft</>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {reasonFor?.action === "reject"
                ? <>The artist will receive an email with your reason and the release will move to <span className="text-red-300">Rejected</span>. They&apos;ll need to duplicate or delete it to try again.</>
                : <>The release goes back to <span className="text-amber-300">Draft</span> so the artist can fix it and resubmit. Your note goes to their inbox and appears on the release.</>}
            </DialogDescription>
          </DialogHeader>
          {reasonFor && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3 mb-1">
              {reasonFor.release.cover_url ? (
                <img src={mediaUrl(reasonFor.release.cover_url)} alt="" className="h-10 w-10 rounded-md object-cover border border-white/10" />
              ) : (
                <div className="h-10 w-10 rounded-md bg-white/5 border border-white/10 flex items-center justify-center">
                  <Disc3 size={16} className="text-slate-500" />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{reasonFor.release.title}</div>
                <div className="text-[11px] text-slate-500 truncate">{reasonFor.release.display_artist || reasonFor.release.artist_name} · {reasonFor.release.type}</div>
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] tracking-[0.15em] uppercase font-medium text-slate-500">
              {reasonFor?.action === "reject" ? "Rejection reason" : "What needs to change?"}
            </label>
            <textarea
              autoFocus
              data-testid="reason-textarea"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder={reasonFor?.action === "reject"
                ? "e.g. Cover art violates our content policy — please replace and submit a fresh release."
                : "e.g. Please add a subgenre and fix the ISRC on track 2."}
              rows={5}
              className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
            <div className="mt-1 text-[11px] text-slate-500">{reasonText.length}/1000 · The artist sees this in their release and via email.</div>
          </div>
          <DialogFooter className="mt-2 gap-2">
            <button
              type="button"
              onClick={() => { setReasonFor(null); setReasonText(""); }}
              className="rounded-full px-4 py-2 text-sm text-slate-300 hover:bg-white/5"
            >Cancel</button>
            <button
              type="button"
              data-testid="reason-submit"
              onClick={submitReason}
              disabled={savingReason || !reasonText.trim()}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${
                reasonFor?.action === "reject"
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : "bg-amber-400 text-black hover:bg-amber-300"
              }`}
            >
              {savingReason && <Loader2 size={14} className="animate-spin" />}
              {reasonFor?.action === "reject" ? "Reject release" : "Send back to draft"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
